import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./Home.css";
import { Link } from "react-router-dom";

import {
collection,
onSnapshot,
doc,
query,
orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";


// ─────────────────────────────
// Animation Variants
// ─────────────────────────────
const containerVariants = {
hidden: { opacity: 0 },
visible: {
opacity: 1,
transition: { staggerChildren: 0.15 },
},
};

const cardVariants = {
hidden: { opacity: 0, y: 25 },
visible: {
opacity: 1,
y: 0,
transition: { duration: 0.4, ease: "easeOut" },
},
};

function Home() {
const [news, setNews] = useState([]);
const [admissionsOpen, setAdmissionsOpen] = useState(true);
const [results, setResults] = useState([]);

const navigate = useNavigate();

// ─────────────────────────────
// News (Realtime, ordered by createdAt desc)
useEffect(() => {
const q = query(collection(db, "news"), orderBy("createdAt", "desc"));
const unsub = onSnapshot(q, (snap) => {
setNews(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
});
return () => unsub();
}, []);

// ─────────────────────────────
// Admissions (Realtime)
useEffect(() => {
const unsub = onSnapshot(doc(db, "settings", "admissions"), (snap) => {
if (snap.exists()) setAdmissionsOpen(snap.data().isOpen);
});
return () => unsub();
}, []);

// ─────────────────────────────
// Results (Realtime, ordered by createdAt desc)
useEffect(() => {
const q = query(collection(db, "results"), orderBy("createdAt", "desc"));
const unsub = onSnapshot(q, (snap) => {
setResults(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
});
return () => unsub();
}, []);

const campusData = [
{ img: "/images/Building3.webp", title: "Main Building" },
{ img: "/images/library.webp", title: "Library" },
{ img: "/images/ground.webp", title: "Sports Ground" },
];

return ( <div className="home">
{/* HERO */}
<section
className="hero"
role="banner"
style={{
background: 'url("/images/Building3.webp") center/cover no-repeat',
backgroundColor: "#0b2e4e",
}}
> <div className="hero-overlay" />
<motion.div
className="hero-text"
initial={{ opacity: 0, scale: 0.9 }}
animate={{ opacity: 1, scale: 1 }}
transition={{ duration: 0.7 }}
> <h1 tabIndex={0}>Welcome to Academic Group of Schools</h1> <p tabIndex={0}>
Shaping the future through quality education and values. </p>
      <div className="hero-buttons">
        <a href="#gallery" className="hero-btn">
          🎓 Explore More
        </a>
        {admissionsOpen ? (
          <button
            className="apply-btn"
            onClick={() => navigate("/ApplyForm")}
          >
            Apply Now
          </button>
        ) : (
          <button className="admission-btn disabled" disabled>
            Admissions Closed
          </button>
        )}
      </div>
    </motion.div>
  </section>

  {/* CAMPUS */}
  <section className="campus">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      🏫 Campus Details
    </motion.h2>
    <p>
      We have three campuses in this city, each designed to provide an
      exceptional learning environment. Together, they span over 50 acres
      and feature modern classrooms, sports facilities, and dedicated spaces
      for arts and sciences.
    </p>
    <motion.div
      className="campus-grid"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {campusData.map((item, index) => (
        <motion.div
          key={index}
          className="campus-card"
          variants={cardVariants}
        >
          <img src={item.img} alt={item.title} loading="lazy" />
          <h4>{item.title}</h4>
        </motion.div>
      ))}
    </motion.div>
  </section>

  {/* FACILITIES */}
  <section className="facilities">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      ⚙️ Our Facilities
    </motion.h2>
    <motion.div
      className="facilities-grid"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {[
        { icon: "🎓", text: "Modern Classrooms with Smart Boards" },
        { icon: "📚", text: "Well-Stocked Library" },
        { icon: "🏀", text: "Sports Complex and Playground" },
        { icon: "🎭", text: "Arts" },
        { icon: "💻", text: "Computer Labs with Latest Technology" },
        { icon: "🍎", text: "Healthy Cafeteria" },
      ].map((facility, index) => (
        <motion.div
          key={index}
          className="facility-card"
          variants={cardVariants}
        >
          <div className="icon">{facility.icon}</div>
          <p>{facility.text}</p>
        </motion.div>
      ))}
    </motion.div>
  </section>

  {/* GALLERY */}
  <section className="gallery" id="gallery">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      📸 Our School Life
    </motion.h2>

    <motion.div
      className="image-grid"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {[
        { img: "/images/boys-branch.webp", alt: "Boys Branch" },
        { img: "/images/boyss.webp", alt: "Boys Student" },
        { img: "/images/girls-branch.webp", alt: "Girls Branch" },
        { img: "/images/kids.webp", alt: "Kids Section" },
      ].map((item, index) => (
        <motion.div key={index} className="card" variants={cardVariants}>
          <img src={item.img} alt="imaage" loading="lazy" />
        </motion.div>
      ))}
    </motion.div>
  </section>

  {/* NEWS */}
  <section className="news-section" aria-labelledby="news-title">
    <motion.h2
      id="news-title"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      📰 Latest Events & News
    </motion.h2>

    <motion.div
      className="news-grid"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
    >
      {news.length > 0 ? (
        news.map((item) => (
          <motion.div
            key={item.id}
            className="news-card"
            variants={cardVariants}
          >
            <h3>{item.title}</h3>
            <span className="news-date">
              📅{" "}
              {item.createdAt
                ? item.createdAt.toDate().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "No Date"}
            </span>
          </motion.div>
        ))
      ) : (
        <p>No events yet.</p>
      )}
    </motion.div>
  </section>

  {/* RESULTS */}
  <section className="results-section" aria-labelledby="results-title">
    <motion.h2
      id="results-title"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      🏆 Student Results
    </motion.h2>

    <motion.div
      className="results-grid"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
    >
      {results.length > 0 ? (
        results.map((res) => (
          <motion.div
            key={res.id}
            className="result-card"
            variants={cardVariants}
          >
            <div className="student-icon">🎓</div>
            <div className="result-footer">
              <span className="result-year">{res.year}</span>
            </div>
            <div className="result-info">
              <h3>{res.name}</h3>
              <p>
                <strong>Roll #:</strong> {res.rollNumber}
              </p>
              <p>
                <strong>Marks:</strong> {res.marks}
              </p>
              <span className="result-date">
                📅{" "}
                {res.createdAt
                  ? res.createdAt.toDate().toLocaleDateString("en-US")
                  : "No Date"}
              </span>
            </div>
          </motion.div>
        ))
      ) : (
        <p>No results announced yet.</p>
      )}
    </motion.div>
  </section>

  {/* CTA */}
  <section className="cta">
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      Ready to Join Us?
    </motion.h2>
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.3 }}
    >
      Contact us today for admission inquiries or to book a campus tour.
    </motion.p>
    <motion.div whileHover={{ scale: 1.1 }}>
      <Link to="/contact" className="contact-btn">
        📞 Contact Us
      </Link>
    </motion.div>
  </section>
</div>


);
}

export default Home;
